import React, { useContext, useState } from 'react'
import { UseFormWatch } from 'react-hook-form'
import { UseFieldArrayUpdate } from 'react-hook-form/dist/types/fieldArray'
import { Control, UseFormRegister, UseFormSetValue } from 'react-hook-form/dist/types/form'
import { IUserInterface, NumberMembershipFieldKey } from '../../../../models/users/user.interface'
import { UserModel } from '../../../../models/users/user.model'

import helpers from '../../../../commonHelpers/helpers'
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { HandleModalContext } from '../../../../layout/mainlayout/MainLayout'
import FirebaseStorageService from '../../../../services/storageService'

import { useAppSelector } from '../../../../store/hooks'
import { selectUserId } from '../../../../store/user/userSlice'

import { CONST } from '../../../../const/const'
import { MESSAGES_CONST } from '../../../../const/messages-const'
import { MODAL_CONSTS } from '../../../../const/modal-const'
import BaseNumberCard from './BaseNumberCard'

interface CompetitorNumberCardProps {
  icon?: string
  label?: string
  fieldKey: NumberMembershipFieldKey
  saved: boolean
  register: UseFormRegister<IUserInterface>
  control: Control<IUserInterface>
  onRemove?: () => void
  userData: IUserInterface | null
  setUserData: (data: IUserInterface) => void
  setValue: UseFormSetValue<IUserInterface>
  update: UseFieldArrayUpdate<IUserInterface, 'userExtraDocument'>
  watch: UseFormWatch<IUserInterface>
}

export const CompetitorNumberCard: React.FC<CompetitorNumberCardProps> = ({
  icon,
  label,
  fieldKey,
  saved,
  register,
  control,
  onRemove,
  userData,
  setUserData,
  setValue,
  update,
  watch,
}) => {
  const userId = useAppSelector(selectUserId)
  const handleModalContext = useContext(HandleModalContext)
  const toastFunctions = useToasterHelper()

  const [isLoading, setIsLoading] = useState<boolean>(false)

  // Helper to check if this is an extraDocument field
  const isExtraDocumentField = (key: string): boolean => {
    return key.startsWith('userExtraDocument.')
  }

  // Helper to get the index from userExtraDocument.X format
  const getExtraDocumentIndex = (key: string): number | null => {
    if (!isExtraDocumentField(key)) return null

    const match = key.match(/userExtraDocument\.(\d+)$/)
    if (match) {
      return parseInt(match[1], 10)
    }
    return null
  }

  // Image upload handler
  const handleImageUpload = async (file: File): Promise<void> => {
    if (!file) {
      toastFunctions.info({ message: MESSAGES_CONST.NO_FILE_SELECTED })
      return
    }

    try {
      setIsLoading(true)
      const PATH = CONST.DATA.STORAGE.USERS.NUMBERS_URL.PREFIX

      const downloadUrl = await FirebaseStorageService.uploadFile(
        file,
        `${PATH}/${userId}/${new Date().getTime()}`,
        (percent: number) => {
          if (0) console.log(percent)
        }
      )

      const currFormValues = watch()

      // Handle userExtraDocument fields
      if (isExtraDocumentField(fieldKey)) {
        const index = getExtraDocumentIndex(fieldKey)
        if (
          index !== null &&
          currFormValues.userExtraDocument &&
          currFormValues.userExtraDocument[index]
        ) {
          update(index, {
            ...currFormValues.userExtraDocument[index],
            image: `${downloadUrl || ''}`,
          })

          // Also update the user data state to ensure UI reflects the change
          if (userData && userData.userExtraDocument) {
            const updatedExtraDocs = [...userData.userExtraDocument]
            updatedExtraDocs[index] = {
              ...updatedExtraDocs[index],
              image: `${downloadUrl || ''}`,
            }

            setUserData({
              ...userData,
              userExtraDocument: updatedExtraDocs,
            })
          }
        }
      }
      // Handle regular membership fields
      else {
        const newUserData = new UserModel({
          ...currFormValues,
          ...userData,
        })

        if ((newUserData as any)[fieldKey]) {
          ;(newUserData as any)[fieldKey].image = (downloadUrl ?? '') as string
        }

        setValue(fieldKey, {
          ...(currFormValues as any)[fieldKey],
          image: `${downloadUrl || ''}`,
        })

        setUserData(newUserData)
      }

      toastFunctions.success({ message: MESSAGES_CONST.PIC_UPLOADED })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      helpers.logger({
        isError: true,
        message: error,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Image remove handler
  const handleImageRemove = async (): Promise<void> => {
    if (!userData) return

    let imgUri = ''

    // Get the image URI based on the field type
    if (isExtraDocumentField(fieldKey)) {
      const index = getExtraDocumentIndex(fieldKey)
      if (index !== null && userData.userExtraDocument) {
        imgUri = userData.userExtraDocument[index]?.image || ''
      }
    } else {
      imgUri = (userData as any)[fieldKey]?.image || ''
    }

    if (!imgUri) return

    try {
      setIsLoading(true)
      await FirebaseStorageService.deleteFile(imgUri)

      const currFormValues = watch()

      // Handle userExtraDocument fields
      if (isExtraDocumentField(fieldKey)) {
        const index = getExtraDocumentIndex(fieldKey)
        if (index !== null && currFormValues.userExtraDocument) {
          update(index, {
            ...currFormValues.userExtraDocument[index],
            image: '',
          })

          // Also update the user data state to ensure UI reflects the change
          if (userData.userExtraDocument) {
            const updatedExtraDocs = [...userData.userExtraDocument]
            updatedExtraDocs[index] = {
              ...updatedExtraDocs[index],
              image: '',
            }

            setUserData({
              ...userData,
              userExtraDocument: updatedExtraDocs,
            })
          }
        }
      }
      // Handle regular membership fields
      else {
        const newUserData = new UserModel({
          ...currFormValues,
          ...userData,
        })

        if ((newUserData as any)[fieldKey]) {
          ;(newUserData as any)[fieldKey].image = ''
        }

        setValue(fieldKey, {
          ...(currFormValues as any)[fieldKey],
          image: '',
        })

        setUserData(newUserData)
      }

      toastFunctions.success({
        message: MESSAGES_CONST.IMAGE_REMOVED,
      })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setIsLoading(false)
    }
  }

  // Confirm image removal with modal
  const confirmRemoveImage = async (): Promise<void> => {
    if (!userData) return

    let imgUri = ''

    // Get the image URI based on the field type
    if (isExtraDocumentField(fieldKey)) {
      const index = getExtraDocumentIndex(fieldKey)
      if (index !== null && userData.userExtraDocument) {
        imgUri = userData.userExtraDocument[index]?.image || ''
      }
    } else {
      imgUri = (userData as any)[fieldKey]?.image || ''
    }

    if (!imgUri) return

    return new Promise<void>((resolve) => {
      handleModalContext?.handleModal(true, MODAL_CONSTS.CONFIRM_REMOVE_IMAGE, {
        confirmDelete: () => {
          handleImageRemove().then(() => resolve())
        },
      })
    })
  }

  // Get the current image URL from user data
  const getImageUrl = (): string => {
    if (!userData) return ''

    // Handle userExtraDocument fields
    if (isExtraDocumentField(fieldKey)) {
      const index = getExtraDocumentIndex(fieldKey)
      if (index !== null && userData.userExtraDocument) {
        return userData.userExtraDocument[index]?.image || ''
      }
      return ''
    }

    // Handle regular membership fields
    return (userData as any)[fieldKey]?.image || ''
  }

  // Get the date field path based on the field type
  const getDateFieldPath = (): string => {
    if (isExtraDocumentField(fieldKey)) {
      const index = getExtraDocumentIndex(fieldKey)
      if (index !== null) {
        return `userExtraDocument.${index}.endDate`
      }
    }
    return `${fieldKey}.endDate`
  }

  return (
    <BaseNumberCard
      icon={icon}
      label={label}
      saved={saved}
      control={control}
      onRemove={onRemove}
      onImageUpload={handleImageUpload}
      onImageRemove={confirmRemoveImage}
      isLoading={isLoading}
      imageUrl={getImageUrl()}
      titleField={
        isExtraDocumentField(fieldKey)
          ? register(`userExtraDocument.${getExtraDocumentIndex(fieldKey)}.documentFullName` as any)
          : register(`${fieldKey}.documentFullName` as any)
      }
      numberField={
        isExtraDocumentField(fieldKey)
          ? register(`userExtraDocument.${getExtraDocumentIndex(fieldKey)}.documentNumber` as any)
          : register(`${fieldKey}.documentNumber` as any)
      }
      dateFieldName={getDateFieldPath()}
      titlePlaceholder="Enter name"
      numberPlaceholder="Enter number"
    />
  )
}
